import React, { useRef } from "react";
import { motion, useInView } from "motion/react";
import { IconCheck } from "@tabler/icons-react";
import { staggerContainer, fadeInUp } from "./AnimationVariants";
import { StoreBadges } from "./StoreBadges";
import progressShot from "../../assets/homepage/product-phone-progress.webp";

const points = [
  "Book a class in seconds, join the waitlist when it's full",
  "See today's workout before class",
  "Log results and watch PRs climb",
];

export function AppSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section
      ref={sectionRef}
      aria-labelledby="app-title"
      className="w-full pt-14 pb-2 md:pt-16 md:pb-2"
    >
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="mx-auto max-w-[1400px] w-[calc(100%-2rem)] md:w-[calc(100%-240px)] text-center lg:text-left"
      >
        <motion.p
          variants={fadeInUp}
          className="text-xs leading-none uppercase tracking-[0.12em] text-white/55"
        >
          The app
        </motion.p>
        <motion.h2
          id="app-title"
          variants={fadeInUp}
          className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight text-white"
        >
          Your members' favourite app.
        </motion.h2>

        <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)] items-start gap-10 md:gap-16 text-center md:text-left">
          {/* Only the top of the phone has content: show that part and fade out before the empty lower screen */}
          <motion.div
            variants={fadeInUp}
            className="mx-auto w-[200px] md:w-full aspect-[603/1019] overflow-hidden [mask-image:linear-gradient(to_bottom,black_80%,transparent)]"
          >
            <img
              src={progressShot}
              alt="Lemonade app: a work capacity score of 79 out of 100 with the benchmark results behind it"
              width={603}
              height={1307}
              loading="lazy"
              className="w-full h-auto rounded-[1.75rem] border border-white/20"
            />
          </motion.div>
          <div className="flex flex-col items-center md:items-start">
            <motion.p variants={fadeInUp} className="max-w-md text-base text-white/60 text-pretty">
              Everything an athlete needs between the door and the whiteboard, in their pocket.
            </motion.p>
            <motion.ul
              variants={fadeInUp}
              className="mt-6 flex flex-col gap-2.5 text-sm text-white/65 text-left"
            >
              {points.map((point) => (
                <li key={point} className="flex gap-2">
                  <IconCheck className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                  {point}
                </li>
              ))}
            </motion.ul>
            <motion.div variants={fadeInUp}>
              <StoreBadges className="mt-8 justify-center md:justify-start" />
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
